import type { SupportApi } from './http';

export interface PresenceState {
  aiResponding: boolean;
  /** 智能客服当前步骤（如“正在查询企业账期…”），没有时为空串。 */
  aiStatus?: string;
  /** 智能客服正在排查这个会话的问题（只给坐席）：坐席已接入时结论会以内部备注发来。 */
  aiInvestigating?: boolean;
  counterpartTyping: boolean;
}

const EMPTY: PresenceState = { aiResponding: false, counterpartTyping: false };

/** 只在聊天可见期间轮询；输入状态由服务端短时保存，绝不发送草稿内容。 */
export class ConversationPresence {
  private readonly path: string;
  private readonly api: SupportApi;
  private readonly onChange: (state: PresenceState) => void;
  private state: PresenceState = EMPTY;
  private running = false;
  private typing = false;
  private generation = 0;
  private pollInFlight: object | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private writeQueue: Promise<void> = Promise.resolve();

  constructor(api: SupportApi, conversationId: string, onChange: (state: PresenceState) => void) {
    this.api = api;
    this.path = `/conversations/${encodeURIComponent(conversationId)}`;
    this.onChange = onChange;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.generation += 1;
    void this.refresh();
    this.pollTimer = setInterval(() => { void this.refresh(); }, 1500);
  }

  stop(): void {
    if (!this.running && !this.typing) return;
    this.running = false;
    this.generation += 1;
    this.pollInFlight = null;
    if (this.pollTimer !== null) clearInterval(this.pollTimer);
    this.pollTimer = null;
    this.clearTyping();
    this.publish(EMPTY);
  }

  async refresh(): Promise<void> {
    if (!this.running || this.pollInFlight) return;
    const poll = {};
    this.pollInFlight = poll;
    const gate = this.generation;
    try {
      const result = await this.api.request<PresenceState>(`${this.path}/presence`);
      if (!this.running || gate !== this.generation) return;
      const aiStatus = result.aiResponding === true && typeof result.aiStatus === 'string' ? result.aiStatus.slice(0, 60) : '';
      this.publish({
        aiResponding: result.aiResponding === true,
        ...(aiStatus ? { aiStatus } : {}),
        ...(result.aiInvestigating === true ? { aiInvestigating: true } : {}),
        counterpartTyping: result.counterpartTyping === true,
      });
    } catch {
      if (this.running && gate === this.generation) this.publish(EMPTY);
    } finally {
      if (this.pollInFlight === poll) this.pollInFlight = null;
    }
  }

  noteInput(nonEmpty: boolean): void {
    if (!this.running || !nonEmpty) {
      this.clearTyping();
      return;
    }
    if (!this.typing) {
      this.typing = true;
      this.writeTyping(true);
      this.heartbeatTimer = setInterval(() => this.writeTyping(true), 3000);
    }
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.clearTyping(), 3500);
  }

  clearTyping(): void {
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    this.idleTimer = null;
    this.heartbeatTimer = null;
    if (!this.typing) return;
    this.typing = false;
    this.writeTyping(false);
  }

  private writeTyping(typing: boolean): void {
    this.writeQueue = this.writeQueue.then(async () => {
      try {
        await this.api.request(`${this.path}/typing`, { body: { typing }, method: 'POST' });
      } catch {
        // 短时状态失败不影响收发；服务端 TTL 会清理未显式关闭的状态。
      }
    });
  }

  private publish(next: PresenceState): void {
    if (this.state.aiResponding === next.aiResponding && (this.state.aiStatus ?? '') === (next.aiStatus ?? '') &&
      (this.state.aiInvestigating ?? false) === (next.aiInvestigating ?? false) &&
      this.state.counterpartTyping === next.counterpartTyping) return;
    this.state = next;
    this.onChange(next);
  }
}
